import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  MapPin,
  ChevronDown,
  Bookmark,
  BookmarkCheck,
  Bell,
  BellRing,
  ExternalLink,
  Filter,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Save,
} from "lucide-react";
import { listJobs, isDemo, trackEvent } from "@/lib/api";
import {
  demoJobs,
} from "@/lib/demoData";
import { locationOptions as buildLocationOptions } from "@/lib/locations";
import { companyLogo, companyInitials } from "@/lib/companyLogo";
import {
  isSaved,
  toggleSaved,
  hasAlert,
  toggleAlert,
  logApplication,
  getSavedSearches,
  saveSearch,
  deleteSavedSearch,
} from "@/lib/storage";
import { useUnlock } from "@/pages/Dashboard";
import { requestLead } from "@/components/dashboard/LeadGateModal";
import { Link } from "react-router-dom";

const LEVELS = ["", "entry", "mid", "senior"];
const TYPES = [
  { id: "", label: "All Jobs" },
  { id: "full-time", label: "Full-time" },
  { id: "part-time", label: "Part-time" },
  { id: "remote", label: "Remote" },
];
const SORTS = [
  { id: "recent", label: "Most Recent" },
  { id: "location", label: "Location" },
];
const SINCES = [
  { id: "", label: "Any time" },
  { id: "24h", label: "Last 24h" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
];
const ATS_SOURCES = [
  "Greenhouse",
  "Lever",
  "Ashby",
  "Workday",
  "ADP",
  "SmartRecruiters",
  "Workable",
];

function fmtCount(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString();
}

/** V1 SEED.cat_names — friendly labels for the ~25 ATS categories. */
const CAT_NAMES = {
  other: "Other",
  engineering: "Engineering",
  "software-engineering": "Software Engineering",
  sales: "Sales",
  healthcare: "Healthcare",
  consulting: "Consulting",
  retail: "Retail",
  "hospitality-food": "Hospitality & Food",
  "customer-support": "Customer Support",
  marketing: "Marketing",
  "finance-accounting": "Finance & Accounting",
  nursing: "Nursing",
  manufacturing: "Manufacturing",
  "science-lab": "Science & Lab",
  "project-management": "Project Management",
  education: "Education",
  "supply-chain": "Supply Chain",
  "real-estate": "Real Estate",
  construction: "Construction",
  transportation: "Transportation",
  government: "Government",
  it: "IT & Infrastructure",
  "it-helpdesk": "IT & Helpdesk",
  legal: "Legal",
  "product-design": "Product & Design",
  "data-analytics": "Data & Analytics",
  "ai-ml": "AI & ML",
  "delivery-driving": "Delivery & Driving",
  "logistics-warehouse": "Logistics & Warehouse",
  cybersecurity: "Cybersecurity",
  "customer-success": "Customer Success",
  "medical-admin": "Medical Admin",
  "hr-recruiting": "HR & Recruiting",
  caregiving: "Caregiving",
  insurance: "Insurance",
  "skilled-trades": "Skilled Trades",
  compliance: "Compliance",
  quality: "Quality",
  administrative: "Administrative",
  "security-safety": "Security & Safety",
  operations: "Operations",
  "energy-utilities": "Energy & Utilities",
};

function prettyCategory(slug) {
  if (!slug) return "—";
  if (CAT_NAMES[slug]) return CAT_NAMES[slug];
  return String(slug)
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format a posted-at ISO timestamp using the V1 reference style:
 *   < 24h → "Today"
 *   1 day → "1d ago" · 6d → "6d ago"
 *   < 30d → "1w ago" … "4w ago"
 *   < 365d → "1mo ago" …
 *   ≥ 1y  → "1y ago" …
 * Falls back to whatever pre-formatted string the row already has.
 */
function fmtPosted(job) {
  const iso = job?.posted_at || job?.postedAt || job?.posted_iso;
  if (iso) {
    const ts = Date.parse(iso);
    if (!Number.isNaN(ts)) {
      const hours = (Date.now() - ts) / 36e5;
      if (hours < 24) return "Today";
      const days = Math.floor(hours / 24);
      if (days === 1) return "1d ago";
      if (days < 7) return `${days}d ago`;
      if (days < 30) return `${Math.floor(days / 7)}w ago`;
      if (days < 365) return `${Math.floor(days / 30)}mo ago`;
      return `${Math.floor(days / 365)}y ago`;
    }
  }
  return job?.posted || "—";
}

/** A row is "new" if posted within ~36h (Today or 1d ago). */
function isNew(job) {
  const label = fmtPosted(job);
  return label === "Today" || label === "1d ago";
}

function CompanyAvatar({ company, domain, applyUrl }) {
  const [errored, setErrored] = useState(false);
  // Prefer Google favicon service (matches the toolbox `logo` field convention)
  // — first try the explicit `domain`, then derive it from the apply URL host,
  // then `companyLogo()` initials.
  let host = domain || null;
  if (!host && applyUrl) {
    try { host = new URL(applyUrl).hostname; } catch { host = null; }
  }
  const primary = host
    ? `https://www.google.com/s2/favicons?domain=${host}&sz=128`
    : companyLogo(company);
  if (errored || !primary) {
    return (
      <div className="w-10 h-10 bg-[#FDFBF7] border-2 border-[#0a0a0a] flex items-center justify-center font-display text-[10px] text-[#0a0a0a]/70">
        {companyInitials(company)}
      </div>
    );
  }
  return (
    <div className="w-10 h-10 bg-white border-2 border-[#0a0a0a] flex items-center justify-center overflow-hidden">
      <img
        src={primary}
        alt={company}
        loading="lazy"
        onError={() => setErrored(true)}
        className="w-full h-full object-contain"
      />
    </div>
  );
}

function Chip({ active, children, onClick, testid, locked, danger }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`font-accent text-[11px] px-3 py-2 border-2 border-[#0a0a0a] inline-flex items-center gap-1.5 hover-pop ${
        active
          ? danger
            ? "bg-[#E10600] text-white shadow-brutal-sm"
            : "bg-[#0a0a0a] text-[#FFC400] shadow-brutal-sm"
          : "bg-white text-[#0a0a0a]"
      } ${locked ? "opacity-60" : ""}`}
    >
      {children}
    </button>
  );
}

function Dropdown({ label, value, onChange, options, icon: Icon, testid, width = "w-48" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function close(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const current = options.find((o) => o.id === value) || options[0];
  return (
    <div className={`relative ${width}`} ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={testid}
        className="w-full font-mono-body text-[13px] bg-white border-2 border-[#0a0a0a] px-3 py-2.5 inline-flex items-center gap-2 hover-pop shadow-brutal-sm"
      >
        {Icon && <Icon size={14} strokeWidth={2.5} />}
        <span className="flex-1 text-left truncate">{current?.label || label}</span>
        <ChevronDown size={14} strokeWidth={3} />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-full bg-white border-4 border-[#0a0a0a] shadow-brutal-sm max-h-72 overflow-y-auto">
          {options.map((o) => (
            <button
              key={o.id}
              onClick={() => {
                onChange(o.id);
                setOpen(false);
              }}
              className={`block w-full text-left px-3 py-2 font-mono-body text-[12px] border-b-2 border-[#0a0a0a]/10 last:border-b-0 ${
                o.id === value ? "bg-[#FFC400] text-[#0a0a0a]" : "hover:bg-[#FDFBF7]"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MoreFiltersDrawer({ open, onClose, filters, setFilters }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-[#0a0a0a]/60" onClick={onClose} />
      <aside className="w-[360px] max-w-full bg-[#FDFBF7] border-l-4 border-[#0a0a0a] p-5 overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div className="font-display text-xl">MORE FILTERS</div>
          <button onClick={onClose} aria-label="Close" className="border-2 border-[#0a0a0a] w-8 h-8 bg-white inline-flex items-center justify-center">
            <X size={16} strokeWidth={3} />
          </button>
        </div>
        <div className="space-y-5">
          <div>
            <div className="font-accent text-[11px] mb-2">DATE POSTED</div>
            <div className="grid grid-cols-2 gap-2">
              {SINCES.map((s) => (
                <Chip
                  key={s.id || "any"}
                  active={filters.since === s.id}
                  onClick={() => setFilters({ ...filters, since: s.id })}
                  testid={`since-${s.id || "any"}`}
                >
                  {s.label}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <div className="font-accent text-[11px] mb-2">ATS SOURCE</div>
            <div className="flex flex-wrap gap-2">
              <Chip active={!filters.ats} onClick={() => setFilters({ ...filters, ats: "" })} testid="ats-all">
                All
              </Chip>
              {ATS_SOURCES.map((a) => (
                <Chip
                  key={a}
                  active={filters.ats === a}
                  onClick={() => setFilters({ ...filters, ats: a })}
                  testid={`ats-${a.toLowerCase()}`}
                >
                  {a}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <div className="font-accent text-[11px] mb-2">COUNTRY</div>
            <div className="flex flex-wrap gap-2">
              <Chip active={filters.country === "US"} onClick={() => setFilters({ ...filters, country: "US" })} testid="country-us">
                US only
              </Chip>
              <Chip active={!filters.country || filters.country === "all"} onClick={() => setFilters({ ...filters, country: "all" })} testid="country-all">
                All countries
              </Chip>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function JobRow({ job, paid, openModal, refreshCounts }) {
  const [saved, setSavedState] = useState(isSaved(job));
  const [alerted, setAlertedState] = useState(hasAlert(job));

  const onSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // First-save email gate (skipped if already captured).
    if (!isSaved(job)) {
      trackEvent("job.save", { url: job.url, company: job.company });
      const gate = await requestLead({
        source: "save-job",
        label: `Save "${job.title}" for later`,
        meta: { job: job.url },
      });
      if (!gate.ok) return;
    }
    const next = toggleSaved(job);
    setSavedState(next);
    refreshCounts?.();
  };
  const onAlert = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasAlert(job)) {
      trackEvent("job.alert.click", { url: job.url, company: job.company });
      const gate = await requestLead({
        source: "alert",
        label: "Get notified about this job",
        meta: { job: job.url },
      });
      if (!gate.ok) return;
    }
    const next = toggleAlert(job);
    setAlertedState(next);
    refreshCounts?.();
  };
  const onApply = (e) => {
    e.preventDefault();
    e.stopPropagation();
    trackEvent("job.apply", { url: job.url, company: job.company, ats: job.ats });
    logApplication(job, "applied");
    refreshCounts?.();
    window.open(job.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      data-testid="job-row"
      className="grid grid-cols-12 gap-3 items-center px-4 md:px-5 py-4 border-b-2 border-[#0a0a0a]/10 hover:bg-[#FDFBF7] transition-colors"
    >
      <div className="col-span-12 md:col-span-4 flex items-start gap-3">
        <CompanyAvatar company={job.company} domain={job.companyDomain || job.domain} applyUrl={job.url || job.apply_url} />
        <div className="min-w-0 flex-1">
          <div className="font-display text-[15px] leading-tight flex items-start gap-2 flex-wrap">
            <span className="truncate">{job.title}</span>
            {isNew(job) && (
              <span className="font-accent text-[9px] bg-[#FFC400] text-[#0a0a0a] border border-[#0a0a0a] px-1.5 py-0.5 leading-none">
                NEW
              </span>
            )}
          </div>
          <div className="font-mono-body text-[11px] text-[#0a0a0a]/65 mt-1 flex items-center gap-2 flex-wrap">
            {job.level && (
              <span className="bg-[#FDFBF7] border border-[#0a0a0a]/30 px-1.5 py-0.5">{job.level}</span>
            )}
            {job.category && (
              <span className="bg-[#FDFBF7] border border-[#0a0a0a]/30 px-1.5 py-0.5">{prettyCategory(job.category)}</span>
            )}
          </div>
        </div>
      </div>
      <div className="col-span-6 md:col-span-2 font-mono-body text-[12px]">
        <div className="font-display text-[13px] text-[#0a0a0a]/85 truncate">{job.company}</div>
        {job.ats && (
          <div className="text-[10px] text-[#0a0a0a]/55">Via {job.ats}</div>
        )}
      </div>
      <div className="col-span-6 md:col-span-3 font-mono-body text-[12px] flex items-center gap-1.5">
        <MapPin size={12} strokeWidth={2.5} className="text-[#0a0a0a]/55 shrink-0" />
        <div className="min-w-0">
          <div className="truncate">{job.location || "—"}</div>
          <div className="text-[10px] text-[#0a0a0a]/55">{job.remote ? "Remote" : "On-site"}</div>
        </div>
      </div>
      <div className="col-span-6 md:col-span-1 font-mono-body text-[12px] text-[#0a0a0a]/75" data-testid="job-posted">
        {fmtPosted(job)}
      </div>
      <div className="col-span-6 md:col-span-2 flex items-center justify-end gap-1.5">
        <button
          onClick={onSave}
          data-testid="job-save"
          aria-label={saved ? "Unsave" : "Save"}
          title={saved ? "Saved" : "Save"}
          className={`hover-pop w-9 h-9 border-2 border-[#0a0a0a] inline-flex items-center justify-center shadow-brutal-sm ${
            saved ? "bg-[#FFC400] text-[#0a0a0a]" : "bg-white"
          }`}
        >
          {saved ? <BookmarkCheck size={14} strokeWidth={3} /> : <Bookmark size={14} strokeWidth={3} />}
        </button>
        <button
          onClick={onAlert}
          data-testid="job-alert"
          aria-label={alerted ? "Remove alert" : "Alert me"}
          title={alerted ? "Alerting" : "Alert me"}
          className={`hover-pop w-9 h-9 border-2 border-[#0a0a0a] inline-flex items-center justify-center shadow-brutal-sm ${
            alerted ? "bg-[#FFC400] text-[#0a0a0a]" : "bg-white"
          }`}
        >
          {alerted ? <BellRing size={14} strokeWidth={3} /> : <Bell size={14} strokeWidth={3} />}
        </button>
        <button
          onClick={onApply}
          data-testid="job-apply"
          className="hover-pop bg-[#E10600] text-white border-2 border-[#0a0a0a] px-3 py-2 font-accent text-[11px] shadow-brutal-sm inline-flex items-center gap-1.5"
        >
          Apply <ExternalLink size={11} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

function SavedSearchesDropdown({ params, setParams }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(getSavedSearches());
  const ref = useRef(null);
  useEffect(() => {
    function close(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const onSave = () => {
    const label = (params.q || params.category || "Search") + (params.loc ? ` · ${params.loc}` : "");
    saveSearch(params, label);
    setList(getSavedSearches());
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onSave}
        data-testid="save-search"
        className="hover-pop bg-white border-2 border-[#0a0a0a] px-3 py-2.5 font-accent text-[11px] shadow-brutal-sm inline-flex items-center gap-1.5"
      >
        <Save size={12} strokeWidth={3} /> Save Search
      </button>
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="saved-searches"
          className="hover-pop bg-white border-2 border-[#0a0a0a] px-3 py-2.5 font-accent text-[11px] shadow-brutal-sm inline-flex items-center gap-1.5"
        >
          Saved <ChevronDown size={11} strokeWidth={3} />
        </button>
        {open && (
          <div className="absolute right-0 z-30 mt-2 w-72 bg-white border-4 border-[#0a0a0a] shadow-brutal-sm">
            {list.length === 0 ? (
              <div className="p-4 font-mono-body text-xs text-[#0a0a0a]/65">
                No saved searches yet. Hit &quot;Save Search&quot; to pin one.
              </div>
            ) : (
              list.map((s) => (
                <div key={s.id} className="flex items-center gap-2 px-3 py-2 border-b-2 border-[#0a0a0a]/10 last:border-b-0">
                  <button
                    onClick={() => {
                      setParams({ ...s.params, page: 1 });
                      setOpen(false);
                    }}
                    className="flex-1 text-left font-mono-body text-[12px] truncate"
                  >
                    {s.label}
                  </button>
                  <button
                    onClick={() => {
                      deleteSavedSearch(s.id);
                      setList(getSavedSearches());
                    }}
                    aria-label="Delete saved search"
                    className="text-[#0a0a0a]/60 hover:text-[#E10600]"
                  >
                    <X size={12} strokeWidth={3} />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardJobs() {
  const { paid, open: openModal } = useUnlock();
  const [params, setParams] = useState({
    q: "",
    loc: "",
    level: "",
    type: "",
    sort: "recent",
    since: "",
    ats: "",
    country: "US",
    category: "",
    page: 1,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [_, forceRender] = useState(0);
  const refreshCounts = () => forceRender((n) => n + 1);

  // Debounced fetch
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const t = setTimeout(async () => {
      // Fire `job.search` once per debounced query change (when user actually
      // typed something). Filter-only changes don't count as a search.
      if (params.q?.trim()) {
        trackEvent("job.search", { q: params.q.trim() });
      }
      const apiParams = { ...params };
      if (apiParams.type === "remote") {
        apiParams.remote = "1";
        delete apiParams.type;
      }
      // Posted-since: UI ids (24h / 7d / 30d) → API days (1 / 7 / 30).
      // "Any time" → omit `since` entirely.
      const sinceMap = { "24h": 1, "7d": 7, "30d": 30 };
      if (apiParams.since in sinceMap) {
        apiParams.since = sinceMap[apiParams.since];
      } else if (!apiParams.since) {
        delete apiParams.since;
      }
      // Location: send 2-letter state code (state=SC), or `remote=1` /
      // `international=1` for the non-state pseudo-locations. Never ship the
      // dropdown's `loc` token or a full state name to the API.
      const locVal = apiParams.loc || "";
      delete apiParams.loc;
      if (locVal === "remote") apiParams.remote = "1";
      else if (locVal === "international") apiParams.international = "1";
      else if (/^[a-z]{2}$/.test(locVal)) apiParams.state = locVal.toUpperCase();
      let res;
      if (isDemo()) {
        res = demoJobs(apiParams);
      } else {
        res = await listJobs(apiParams);
        // Normalise legacy shape if backend returns `jobs[]` instead of `results[]`
        if (res?.ok && Array.isArray(res.jobs) && !Array.isArray(res.results)) {
          res.results = res.jobs;
        }
        if (res?.error === "login_required" || res?.ok === false) {
          res = { ok: false, login_required: true };
        }
      }
      if (!cancel) {
        setData(res);
        setLoading(false);
      }
    }, 250);
    return () => {
      cancel = true;
      clearTimeout(t);
    };
  }, [params]);

  const total = data?.total ?? null;
  const jobs = data?.results || data?.jobs || [];
  // /api/jobs returns `facets.categories` as a {slug: count} map. Normalize
  // to a sorted list with friendly display names. Fallback to legacy shapes.
  const categories = useMemo(() => {
    const raw = data?.facets?.categories ?? data?.categories ?? null;
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw.map((c) => ({
        slug: c.slug || c.id || c.name,
        name: c.name || c.label || prettyCategory(c.slug || c.id || ""),
        count: c.count ?? 0,
      }));
    }
    // Object map
    return Object.entries(raw)
      .map(([slug, count]) => ({ slug, name: prettyCategory(slug), count: Number(count) || 0 }))
      .sort((a, b) => b.count - a.count);
  }, [data]);
  const page = data?.page || params.page;
  const pages = data?.pages || 1;

  const setParam = (k, v) => setParams((p) => ({ ...p, [k]: v, page: 1 }));

  const locationOptions = useMemo(() => buildLocationOptions(), []);

  return (
    <div data-testid="dash-jobs" data-tour="jobs-root" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl">JOB DIRECTORY</h1>
          <p className="font-mono-body text-sm text-[#0a0a0a]/65 mt-1.5 max-w-xl">
            Search live roles from 18,000+ company boards — apply direct, no middleman.
          </p>
        </div>
        <SavedSearchesDropdown params={params} setParams={setParams} />
      </div>

      {/* Search row */}
      <div data-tour="jobs-search" className="grid grid-cols-12 gap-3">
        <div className="col-span-12 md:col-span-8">
          <div className="relative">
            <Search
              size={16}
              strokeWidth={2.5}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0a0a0a]/55"
            />
            <input
              data-testid="jobs-search-input"
              value={params.q}
              onChange={(e) => setParam("q", e.target.value)}
              placeholder="Search jobs by title, company, or keyword…"
              className="w-full bg-white border-2 border-[#0a0a0a] pl-10 pr-4 py-3 font-mono-body text-[13px] shadow-brutal-sm focus:outline-none focus:bg-[#FDFBF7]"
            />
          </div>
        </div>
        <div className="col-span-12 md:col-span-4">
          <Dropdown
            value={params.loc}
            onChange={(v) => setParam("loc", v)}
            options={locationOptions}
            icon={MapPin}
            testid="jobs-location"
            width="w-full"
          />
        </div>
      </div>

      {/* Filter row */}
      <div data-tour="jobs-filters" className="flex items-center gap-2 flex-wrap">
        <div className="font-accent text-[10px] text-[#0a0a0a]/55 mr-1">LEVEL</div>
        {LEVELS.map((lv) => (
          <Chip
            key={lv || "any"}
            active={params.level === lv}
            onClick={() => setParam("level", lv)}
            testid={`level-${lv || "any"}`}
          >
            {lv ? lv[0].toUpperCase() + lv.slice(1) + "-level" : "Any level"}
          </Chip>
        ))}
        <Chip onClick={() => setShowFilters(true)} testid="more-filters">
          <Filter size={11} strokeWidth={3} /> More Filters
        </Chip>
        <div className="flex-1" />
        <div className="font-accent text-[10px] text-[#0a0a0a]/55 mr-1">TYPE</div>
        {TYPES.map((t) => (
          <Chip
            key={t.id || "all"}
            active={(params.type || "") === t.id}
            onClick={() => setParam("type", t.id)}
            testid={`type-${t.id || "all"}`}
            danger={t.id === "" && (params.type || "") === ""}
          >
            {t.label}
          </Chip>
        ))}
        <Dropdown
          value={params.sort}
          onChange={(v) => setParam("sort", v)}
          options={SORTS}
          testid="jobs-sort"
          width="w-44"
        />
      </div>

      <MoreFiltersDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={params}
        setFilters={(next) => setParams({ ...next, page: 1 })}
      />

      {/* Count + pagination */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="font-mono-body text-[13px] flex items-center gap-3">
          <span data-testid="jobs-count">
            {total == null ? (
              <span
                aria-hidden="true"
                data-testid="jobs-count-skeleton"
                className="inline-block align-middle h-4 w-24 bg-[#0a0a0a]/10 animate-pulse"
              />
            ) : (
              <b className="font-display text-[#0a0a0a]">{fmtCount(total)}</b>
            )}{" "}
            jobs found
          </span>
          {!loading && data?.ok !== false && <span className="inline-flex items-center gap-1.5"><span className="live-dot" /> live</span>}
          {loading && <span className="inline-flex items-center gap-1.5 text-[#0a0a0a]/65"><Loader2 size={12} className="animate-spin" /> refreshing…</span>}
        </div>
        <Pagination
          page={page}
          pages={pages}
          onChange={(p) => setParams((prev) => ({ ...prev, page: p }))}
        />
      </div>

      {/* Body */}
      <div className="grid grid-cols-12 gap-6">
        {/* Job list */}
        <div className="col-span-12 lg:col-span-9 bg-white border-4 border-[#0a0a0a] shadow-brutal">
          <div className="grid grid-cols-12 gap-3 px-4 md:px-5 py-3 bg-[#0a0a0a] text-[#FFC400] font-accent text-[10px]">
            <div className="col-span-12 md:col-span-4">JOB TITLE</div>
            <div className="col-span-6 md:col-span-2">COMPANY</div>
            <div className="col-span-6 md:col-span-3">LOCATION</div>
            <div className="col-span-6 md:col-span-1">POSTED</div>
            <div className="col-span-6 md:col-span-2 text-right">ACTIONS</div>
          </div>
          {data?.ok === false && (
            <div className="px-6 py-10 text-center">
              <div className="font-display text-lg mb-2">Log in on jobhacki.com to see live jobs.</div>
              <p className="font-mono-body text-sm text-[#0a0a0a]/65 mb-5">
                Live ATS listings require your JobHacki session. Sign in once and this directory becomes real.
              </p>
              <a
                href="https://jobhacki.com/login"
                target="_blank"
                rel="noopener noreferrer"
                className="hover-pop inline-flex items-center gap-2 bg-[#E10600] text-white border-2 border-[#0a0a0a] px-4 py-2.5 font-accent text-sm shadow-brutal-sm"
              >
                Open login on jobhacki.com <ExternalLink size={12} strokeWidth={3} />
              </a>
            </div>
          )}
          {data?.ok !== false && jobs.length === 0 && !loading && (
            <div className="px-6 py-10 font-mono-body text-sm text-[#0a0a0a]/70 text-center">
              No roles match your filters. Try clearing some.
            </div>
          )}
          {jobs.map((j, i) => (
            <JobRow
              key={(j.url || j.title) + i}
              job={j}
              paid={paid}
              openModal={openModal}
              refreshCounts={refreshCounts}
            />
          ))}
        </div>

        {/* Category sidebar */}
        <aside data-tour="jobs-categories" className="col-span-12 lg:col-span-3">
          <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal">
            <div className="px-4 py-3 bg-[#0a0a0a] text-[#FFC400] font-accent text-[10px]">
              CATEGORIES
            </div>
            <button
              onClick={() => setParam("category", "")}
              data-testid="cat-all"
              className={`flex items-center justify-between w-full px-4 py-3 border-b-2 border-[#0a0a0a]/10 hover:bg-[#FDFBF7] ${
                !params.category ? "bg-[#FFC400]/15" : ""
              }`}
            >
              <span className="font-mono-body text-[13px]">All Categories</span>
              <span className="font-accent text-[11px] text-[#E10600]">{fmtCount(total)}</span>
            </button>
            {(categories.length ? categories : []).map((c) => (
              <button
                key={c.slug}
                onClick={() => setParam("category", c.slug)}
                data-testid={`cat-${c.slug}`}
                className={`flex items-center justify-between w-full px-4 py-2.5 border-b-2 border-[#0a0a0a]/10 last:border-b-0 hover:bg-[#FDFBF7] ${
                  params.category === c.slug ? "bg-[#FFC400]/15" : ""
                }`}
              >
                <span className="font-mono-body text-[12px] truncate">{c.name}</span>
                <span className="font-mono-body text-[11px] text-[#0a0a0a]/55">{fmtCount(c.count)}</span>
              </button>
            ))}
            {categories.length === 0 && data?.ok !== false && (
              <div className="px-4 py-6 font-mono-body text-[11px] text-[#0a0a0a]/55">
                Category counts appear once results load.
              </div>
            )}
          </div>
          <div className="mt-4">
            <Link
              to="/dashboard/saved"
              className="block hover-pop bg-white border-2 border-[#0a0a0a] px-3 py-2.5 font-accent text-[11px] shadow-brutal-sm"
            >
              View Saved Jobs →
            </Link>
            <Link
              to="/dashboard/alerts"
              className="mt-2 block hover-pop bg-white border-2 border-[#0a0a0a] px-3 py-2.5 font-accent text-[11px] shadow-brutal-sm"
            >
              Manage Job Alerts →
            </Link>
          </div>
        </aside>
      </div>

      <div className="flex justify-end">
        <Pagination
          page={page}
          pages={pages}
          onChange={(p) => setParams((prev) => ({ ...prev, page: p }))}
        />
      </div>
    </div>
  );
}

function Pagination({ page, pages, onChange }) {
  const min = 1;
  const max = Math.min(500, pages);
  const around = [page - 1, page, page + 1].filter((p) => p > min && p < max);
  const pageBtns = Array.from(new Set([min, ...around, max])).filter((p) => p >= 1 && p <= max);
  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        aria-label="Previous page"
        className="w-9 h-9 border-2 border-[#0a0a0a] bg-white shadow-brutal-sm hover-pop inline-flex items-center justify-center disabled:opacity-40"
      >
        <ChevronLeft size={14} strokeWidth={3} />
      </button>
      {pageBtns.map((p, i) => {
        const prev = pageBtns[i - 1];
        const gap = prev != null && p - prev > 1;
        return (
          <React.Fragment key={p}>
            {gap && <span className="px-1 font-mono-body text-[12px] text-[#0a0a0a]/55">…</span>}
            <button
              onClick={() => onChange(p)}
              className={`min-w-9 h-9 px-2 border-2 border-[#0a0a0a] shadow-brutal-sm font-accent text-[11px] ${
                p === page ? "bg-[#E10600] text-white" : "bg-white"
              }`}
            >
              {p}
            </button>
          </React.Fragment>
        );
      })}
      <button
        disabled={page >= max}
        onClick={() => onChange(page + 1)}
        aria-label="Next page"
        className="w-9 h-9 border-2 border-[#0a0a0a] bg-white shadow-brutal-sm hover-pop inline-flex items-center justify-center disabled:opacity-40"
      >
        <ChevronRight size={14} strokeWidth={3} />
      </button>
    </div>
  );
}
